import { useEffect, useRef, useState } from 'react';
import {
  buildStyles,
  CircularProgressbarWithChildren,
} from 'react-circular-progressbar';
import 'react-circular-progressbar/dist/styles.css';
import { getStandardAssessmentScore } from '@api/backend';
import { animated, useSpring } from '@react-spring/web';
import Box from '@retracedgmbh/ui-components-lib/Box';
import Pill from '@retracedgmbh/ui-components-lib/Pill';
import Tooltip from '@retracedgmbh/ui-components-lib/Tooltip';
import Typography from '@retracedgmbh/ui-components-lib/Typography';
import { useNavigate } from 'react-router-dom';
import { useQuery, useQueryClient } from '@/hooks/useApi';
import { useNavbar } from '@/state/navbar';
import ListItem from './ListItem';

const AuditAINotification = ({ badgeCount }) => {
  const [percentage, setPercentage] = useState(0);
  const [showIcon, setShowIcon] = useState(true);
  const [showDropdown, setShowDropdown] = useState(false);
  const [inProgress, setInProgress] = useState(true);

  const queryClient = useQueryClient();

  const navigate = useNavigate();
  const { auditAI, buttonPosition, setAuditAI } = useNavbar(state => ({
    auditAI: state.auditAI,
    buttonPosition: state.buttonPosition,
    setAuditAI: state.setAuditAI,
  }));

  const [props, set] = useSpring(() => ({ transform: 'translateY(100vh)' }));

  useEffect(() => {
    const timer = setInterval(() => {
      setShowIcon(prevShowIcon => !prevShowIcon);
    }, 2000); // Change icon every 2 seconds

    return () => clearInterval(timer);
  }, []);

  useEffect(() => {
    const progressTimer = setInterval(() => {
      setPercentage(oldPercentage => {
        if (oldPercentage >= 80) {
          setInProgress(false);
          clearInterval(progressTimer);
          return 80;
        }
        return oldPercentage + 1;
      });
    }, 1500); // Increase percentage every 1.5 seconds

    return () => clearInterval(progressTimer);
  }, []);

  const dropdownRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = () => {
      if (showDropdown) {
        setShowDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showDropdown]);

  const { data } = useQuery(
    ['standardAssessmentScore', auditAI.auditID],
    () => getStandardAssessmentScore(auditAI.auditID),
    {
      refetchInterval: 30000, // Poll every 30 seconds
      enabled: inProgress,
    },
  );

  useEffect(() => {
    const response = data?.data?.data;
    if (response && response.status === 'READY') {
      setInProgress(false);
      queryClient.setQueryData(
        ['standardAssessmentScore', auditAI.auditID],
        oldData => ({ ...oldData }),
        {
          staleTime: Number.POSITIVE_INFINITY,
        },
      );
    }
  }, [data]);

  useEffect(() => {
    if (!inProgress && percentage === 80) {
      setAuditAI({ processing: false, done: true });
      setPercentage(100);
    }
  }, [inProgress, percentage]);

  useEffect(() => {
    if (buttonPosition) {
      const { top } = buttonPosition;
      set({
        to: { transform: 'translateY(10px)' },
        from: { transform: `translateY(${top}px)` },
      });
    }
  }, [buttonPosition]);

  const handleAuditNotificationClick = () => {
    if (auditAI.processing) return;
    navigate(`/audits/${auditAI.auditID}/view?tab=overview-experimental`);
    setAuditAI({ processing: false, done: false, auditID: '' });
  };

  return (
    <animated.div style={props}>
      <Box
        flex
        align='center'
        justify='center'
        onClick={handleAuditNotificationClick}
      >
        <Tooltip
          placement='bottom'
          title={auditAI.processing ? 'Extracting Audit Information...' : null}
        >
          <Box
            className={`tw:h-full tw:mr-5 ${auditAI.processing ? 'tw:cursor-disallowed' : 'tw:cursor-pointer'} tw:hover:bg-background`}
          >
            <CircularProgressbarWithChildren
              className='tw:h-10 tw:relative'
              value={percentage}
              styles={buildStyles({
                strokeLinecap: 'butt',
                pathColor: percentage < 100 ? '#325269' : '#8CC486',
              })}
            >
              <Box
                flex
                align='center'
                className={`tw:h-10 tw:transition-opacity tw:duration-500 ${showIcon ? 'tw:opacity-100' : 'tw:opacity-0'}`}
              >
                <i className='fa-duotone fa-microchip-ai tw:text-3xl tw:text-primary' />
              </Box>
              <Box
                className={`tw:h-10 tw:absolute tw:top-1/3 tw:left-1/5 tw:text-xs tw:text-primary tw:font-semibold tw:transition-opacity tw:duration-500 ${showIcon ? 'tw:opacity-0' : 'tw:opacity-100'}`}
              >
                {percentage.toString().padStart(2, '0')}%
              </Box>
              {badgeCount && (
                <Box className='tw:absolute tw:top-0 tw:-right-2'>
                  <Box
                    flex
                    align='center'
                    justify='center'
                    className='tw:h-5 tw:w-5 tw:bg-danger tw:rounded-full'
                  >
                    <Typography variant='caption' color='white'>
                      {badgeCount}
                    </Typography>
                  </Box>
                </Box>
              )}
            </CircularProgressbarWithChildren>
          </Box>
        </Tooltip>
        {showDropdown ? (
          <Box className='tw:relative' ref={dropdownRef}>
            <Box className='tw:w-64 tw:bg-white tw:border tw:border-custom-gray tw:shadow-sm tw:rounded-md tw:absolute tw:top-7 tw:right-4'>
              <Box flex justify='between' className='tw:p-4 tw:border-b'>
                <Typography
                  variant='h6'
                  fontWeight='semibold'
                  className='tw:text-primary'
                >
                  Audit AI Results
                </Typography>
                <Box flex align='center'>
                  <Pill title='3 items' variant='squared' size='sm' />
                </Box>
              </Box>
              <ListItem
                title='Risk Assessment'
                subtitle='Company net score -'
                result='1.2'
                color='success'
              />
              <ListItem
                title='CAPAs'
                subtitle='Created -'
                result='3'
                color='primary'
              />
              <ListItem
                title='Findings'
                subtitle='Created -'
                result='3'
                color='primary'
              />
            </Box>
          </Box>
        ) : null}
      </Box>
    </animated.div>
  );
};

export default AuditAINotification;
